import * as React from "react";
import { useDispatch } from "react-redux";

import {
	DataSelectors,
	Events,
	ModelSelectors,
	UiStateSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel, FormModelPath } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import {
	DefaultWidgetMap,
	FormModelMap,
	useDocumentPathForInput
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { localizableFromModel } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { maxRepeatabilityReached } from "../../../utilities/inlineRepeatHelper";
import { TEXTBAUSTEINE_ANZEIGEN, EMAIL_VERSAND } from "../../../utilities/variables/modulfRuntimeAnnotations";

import { openTextTree } from "../sagas/actions";

interface NachforderungRepeatButtonsProps {
	readonly activityId: string;
	readonly marker: string;
	readonly disabled?: boolean;
	readonly inlineProps: FormModelMap.FormModelComponentProps<FormModel.InlineRepeat>;
	readonly kategorie?: string;
}

export function NachforderungRepeatButtons(props: NachforderungRepeatButtonsProps) {
	const dispatch = useDispatch();

	const { activityId, inlineProps, marker, disabled, kategorie } = props;

	const uiDisabled = UiStateSelectors.disabled()(inlineProps.config.renderOptions.state);
	const document = DataSelectors.document()(inlineProps.config.renderOptions.state) as GroupInstance;
	const documentModel = ModelSelectors.documentModel()(inlineProps.config.renderOptions.state);

	const path = useDocumentPathForInput(inlineProps.modelElement.groupPath, documentModel);
	const repeatFormModelPath = FormModelPath.extend(inlineProps.config.parentPath, inlineProps.modelElement);

	const formModel = props.inlineProps.config.renderOptions.state.models.formModel;
	const { localizer } = React.useContext(LocalizerContext);
	const addLabel = localizer(
		localizableFromModel(
			`uiModel.${formModel.header.id}.defaults.buttonLabel.add`,
			inlineProps.modelElement.buttonLabels?.ADD?.text ?? [{ locale: "de", text: "Hinzufügen" }]
		)
	);

	const zeigeTextbausteineValue = props.inlineProps.modelElement.annotations?.find(
		({ name }) => name === TEXTBAUSTEINE_ANZEIGEN
	)?.value;
	const zeigeTextbausteine = toBoolean(zeigeTextbausteineValue);

	const addRow = React.useCallback(() => {
		dispatch(
			FormEngineActions.command({
				activityId,
				engineEvent: Events.Repeat.addRow({ path, repeatFormModelPath })
			})
		);
	}, [dispatch, activityId, path, repeatFormModelPath]);

	const onTextbausteine = React.useCallback(() => {
		const targetFieldName = inlineProps.modelElement.repeatOverviewColumn
			?.flatMap(col => (col as { annotations?: { name: string; value: string }[] }).annotations ?? [])
			.find(a => a.name === EMAIL_VERSAND)?.value;
		dispatch(
			openTextTree({
				initiatingActivityId: activityId,
				fvKey: marker,
				repeatableGroupPath: path,
				kategorie,
				targetFieldName
			})
		);
	}, [dispatch, activityId, marker, path, kategorie, inlineProps.modelElement.repeatOverviewColumn]);

	return !maxRepeatabilityReached(path, documentModel, document) ? (
		<DefaultWidgetMap.ButtonGroup className="-u-padding-b-base">
			<DefaultWidgetMap.Button
				label={addLabel || "Neuer Text"}
				icon={<DefaultWidgetMap.Icon>add</DefaultWidgetMap.Icon>}
				disabled={disabled || uiDisabled}
				onClick={addRow}
			/>
			{zeigeTextbausteine ? (
				<DefaultWidgetMap.Button
					label="Textbausteine"
					icon={<DefaultWidgetMap.Icon>library_books</DefaultWidgetMap.Icon>}
					disabled={disabled || uiDisabled}
					onClick={onTextbausteine}
				/>
			) : (
				<></>
			)}
		</DefaultWidgetMap.ButtonGroup>
	) : null;

	function toBoolean(value: string | undefined): boolean {
		if (value === "true") {
			return true;
		} else if (value === "false") {
			return false;
		} else {
			throw new Error(`Der Konfigurationswert ${value} ist nicht als Boolean konfiguriert.`);
		}
	}
}
